import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLBookmarks = () => {
    const simpleAnchorCode = `<!DOCTYPE html>
<html>
<head>
    <title>Anchor Links Demo</title>
</head>
<body>
    <!-- Link that jumps to a section --&gt;
    <nav>
        <a href="#introduction">Introduction</a>
        <a href="#features">Features</a>
        <a href="#pricing">Pricing</a>
        <a href="#contact">Contact</a>
    </nav>

    <!-- Target sections with matching IDs --&gt;
    <section id="introduction">
        <h2>Introduction</h2>
        <p>Content here...</p>
    </section>

    <section id="features">
        <h2>Features</h2>
        <p>Content here...</p>
    </section>

    <section id="pricing">
        <h2>Pricing</h2>
        <p>Content here...</p>
    </section>

    <section id="contact">
        <h2>Contact</h2>
        <p>Content here...</p>
    </section>
</body>
</html>`;

    const tocCode = `<article>
    <header>
        <h1>Complete HTML Guide</h1>
        
        <!-- Table of Contents --&gt;
        <nav aria-label="Table of contents">
            <h2>Table of Contents</h2>
            <ol>
                <li><a href="#what-is-html">What is HTML?</a></li>
                <li><a href="#getting-started">Getting Started</a></li>
                <li><a href="#basic-tags">Basic Tags</a>
                    <ol>
                        <li><a href="#headings">Headings</a></li>
                        <li><a href="#paragraphs">Paragraphs</a></li>
                        <li><a href="#links">Links</a></li>
                    </ol>
                </li>
                <li><a href="#best-practices">Best Practices</a></li>
            </ol>
        </nav>
    </header>

    <main>
        <section id="what-is-html">
            <h2>What is HTML?</h2>
            <p>HTML stands for HyperText Markup Language...</p>
        </section>

        <section id="getting-started">
            <h2>Getting Started</h2>
            <p>To begin with HTML...</p>
        </section>

        <section id="basic-tags">
            <h2>Basic Tags</h2>
            
            <h3 id="headings">Headings</h3>
            <p>HTML has six heading levels...</p>
            
            <h3 id="paragraphs">Paragraphs</h3>
            <p>Paragraphs are created with...</p>
            
            <h3 id="links">Links</h3>
            <p>Links connect pages together...</p>
        </section>

        <section id="best-practices">
            <h2>Best Practices</h2>
            <p>Follow these guidelines...</p>
        </section>
    </main>
</article>`;

    const backToTopCode = `<body>
    <!-- Add ID to top element --&gt;
    <header id="top">
        <h1>Page Title</h1>
        <nav>...</nav>
    </header>

    <main>
        <!-- Long content --&gt;
        <p>Lots of content...</p>
    </main>

    <footer>
        <!-- Back to top link --&gt;
        <a href="#top" class="back-to-top"> Back to Top</a>
    </footer>
</body>

<style>
.back-to-top {
    display: inline-block;
    padding: 0.75rem 1.5rem;
    background-color: #007bff;
    color: white;
    text-decoration: none;
    border-radius: 5px;
    transition: background-color 0.3s;
}

.back-to-top:hover {
    background-color: #0056b3;
}

/* Fixed position button (alternative) */
.back-to-top.fixed {
    position: fixed;
    bottom: 20px;
    right: 20px;
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}
</style>`;

    const cssSmoothScrollCode = `/* Apply to entire page */
html {
    scroll-behavior: smooth;
}

/* Or apply to specific container */
.scrollable-container {
    scroll-behavior: smooth;
}`;

    const mediaQueryScrollCode = `/* Only apply smooth scrolling if user hasn't requested reduced motion */
@media (prefers-reduced-motion: no-preference) {
    html {
        scroll-behavior: smooth;
    }
}

/* Instant scrolling for users who prefer reduced motion */
@media (prefers-reduced-motion: reduce) {
    html {
        scroll-behavior: auto;
    }
}`;

    const jsSmoothScrollCode = `<a href="#section-2" class="smooth-scroll">Jump to Section 2</a>

<script>
document.querySelectorAll('a.smooth-scroll').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
        e.preventDefault();
        
        const targetId = this.getAttribute('href');
        const targetElement = document.querySelector(targetId);
        
        targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
        
        // Update URL without jumping
        history.pushState(null, null, targetId);
    });
});
</script>`;

    const scrollPaddingCode = `/* If you have a 60px fixed header */
header {
    position: fixed;
    top: 0;
    height: 60px;
    background: white;
    z-index: 100;
}

/* Add scroll padding to account for fixed header */
html {
    scroll-padding-top: 70px; /* Header height + 10px buffer */
}

/* Or apply to specific sections */
section {
    scroll-margin-top: 70px;
}`;

    const invisibleAnchorCode = `<section>
    <!-- Invisible anchor positioned above visible heading --&gt;
    <span id="features" class="anchor-offset"></span>
    <h2>Features</h2>
    <p>Content...</p>
</section>

<style>
.anchor-offset {
    display: block;
    position: relative;
    top: -70px; /* Negative offset for fixed header */
    visibility: hidden;
}
</style>`;

    const intersectionObserverCode = `<nav id="toc">
    <a href="#section-1" class="toc-link">Section 1</a>
    <a href="#section-2" class="toc-link">Section 2</a>
    <a href="#section-3" class="toc-link">Section 3</a>
</nav>

<section id="section-1">...</section>
<section id="section-2">...</section>
<section id="section-3">...</section>

<style>
.toc-link.active {
    font-weight: bold;
    color: #007bff;
    border-left: 3px solid #007bff;
    padding-left: 8px;
}
</style>

<script>
const sections = document.querySelectorAll('section[id]');
const tocLinks = document.querySelectorAll('.toc-link');

const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            // Remove active class from all links
            tocLinks.forEach(link => link.classList.remove('active'));
            
            // Add active class to current section's link
            const activeLink = document.querySelector(\`.toc-link[href="#\${entry.target.id}"]\`);
            if (activeLink) {
                activeLink.classList.add('active');
            }
        }
    });
}, {
    rootMargin: '-20% 0px -70% 0px' // Trigger when section is in viewport
});

sections.forEach(section => observer.observe(section));
</script>`;

    const faqAccordionCode = `<section class="faq">
    <h2>Frequently Asked Questions</h2>
    
    <div class="faq-item" id="faq-1">
        <h3>
            <a href="#faq-1">What is HTML?</a>
        </h3>
        <p>HTML stands for HyperText Markup Language...</p>
    </div>
    
    <div class="faq-item" id="faq-2">
        <h3>
            <a href="#faq-2">Is HTML a programming language?</a>
        </h3>
        <p>No, HTML is a markup language, not a programming language...</p>
    </div>
    
    <div class="faq-item" id="faq-3">
        <h3>
            <a href="#faq-3">Do I need to know CSS?</a>
        </h3>
        <p>While you can write HTML without CSS...</p>
    </div>
</section>

<style>
.faq-item {
    margin: 1.5rem 0;
    padding: 1rem;
    border: 1px solid #ddd;
    border-radius: 5px;
}

.faq-item:target {
    background-color: #fff3cd;
    border-color: #ffc107;
}

.faq-item h3 a {
    color: inherit;
    text-decoration: none;
}

.faq-item h3 a:hover {
    text-decoration: underline;
}
</style>`;

    const targetStylingCode = `/* Highlight targeted section */
section:target {
    background-color: #ffffcc;
    border-left: 5px solid #ffcc00;
    padding-left: 15px;
    animation: highlight 2s ease-out;
}

@keyframes highlight {
    0% {
        background-color: #ffff00;
    }
    100% {
        background-color: #ffffcc;
    }
}

/* Add indicator to targeted heading */
h2:target::before {
    content: " ";
    color: #007bff;
    font-weight: bold;
}

/* Pulse animation for targeted element */
:target {
    animation: pulse 1s ease-in-out;
}

@keyframes pulse {
    0%, 100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.02);
    }
}`;

    const urlFragmentCode = `// Get current fragment
const currentFragment = window.location.hash; // "#section-2"

// Remove the # symbol
const fragmentId = currentFragment.substring(1); // "section-2"

// Set fragment programmatically
window.location.hash = "section-3";

// Scroll to element without changing URL
document.getElementById('section-2').scrollIntoView();

// Listen for hash changes
window.addEventListener('hashchange', () => {
    console.log('Fragment changed to:', window.location.hash);
});

// Check if page loaded with fragment
if (window.location.hash) {
    const target = document.querySelector(window.location.hash);
    if (target) {
        // Wait for page load then scroll
        window.addEventListener('load', () => {
            target.scrollIntoView({ behavior: 'smooth' });
        });
    }
}`;

    const focusManagementCode = `// Move focus to target element after scrolling
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
        e.preventDefault();
        
        const targetId = this.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
            // Scroll to element
            targetElement.scrollIntoView({ behavior: 'smooth' });
            
            // Set focus (make element focusable first if needed)
            if (!targetElement.hasAttribute('tabindex')) {
                targetElement.setAttribute('tabindex', '-1');
            }
            targetElement.focus();
        }
    });
});`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Deep Linking & UX</div>
                <h1>The Art of Page Anchors & Bookmarks</h1>
                <p className="hero-lead">
                    Transform your long-form content into an interactive roadmap. Master URL
                    fragments, the <code>:target</code> pseudo-class, and advanced scroll-orchestration
                    to create professional-grade Single Page Navigation.
                </p>

                <ProTip><p><strong>Senior Engineering Insight:</strong> In modern web applications,
                        anchors are the backbone of "Deep Linking." They allow users to share specific
                        states or sections of a page, significantly increasing user retention
                        and conversion rates by reducing the "search friction" within your UI.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>1. The Anatomy of a URL Fragment</h2>
                <p>
                    A page anchor relies on the <strong>URL Fragment</strong> (the part of a URL
                    starting with the <code>#</code> character). Unlike query parameters
                    (<code>?id=123</code>), fragments are handled entirely on the
                    <strong>client-side</strong>. The browser does not send the fragment
                    to the server; it uses it to locate an element with a matching
                    ID in the DOM.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Identifier Match</h3>
                        <p>The browser searches for any element where <code>id="fragment"</code>.
                            If found, it calculates the element's coordinate and scrolls the
                            viewport.</p>
                    </div>
                    <div className="concept-card">
                        <h3>The Null Fragment (#)</h3>
                        <p>A bare <code>#</code> is a special case that always scrolls the
                            user back to the top of the <code>&lt;html&gt;</code> element.</p>
                    </div>
                </div>

                <ProTip><p><strong>Technical Note:</strong> Historically, developers used
                        <code>&lt;a name="target"&gt;</code> for anchors. This is
                        <strong>obsolete</strong>. Modern HTML5 uses the <code>id</code>
                        attribute on <em>any</em> element as a valid anchor target.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Strategic Table of Contents (TOC)</h2>
                <p>
                    A Table of Contents is the most common implementation of anchors.
                    Beyond mere navigation, it provides users with a visual mental map
                    of your content's hierarchy.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Feature</th>
                                <th>Static TOC</th>
                                <th>Sticky/Floating TOC</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Visibility</td>
                                <td>Only at the top</td>
                                <td>Always visible while scrolling</td>
                            </tr>
                            <tr>
                                <td>UX Value</td>
                                <td>Good for initial overview</td>
                                <td>Excellent for switching sections mid-read</td>
                            </tr>
                            <tr>
                                <td>Implementation</td>
                                <td>Standard List of Links</td>
                                <td>CSS <code>position: sticky</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ProTip><p><strong>Senior UX Tip:</strong> For extremely long technical
                        articles, a sticky sidebar TOC with <strong>active state tracking</strong>
                        (using Intersection Observer) is the gold standard. It tells the
                        user exactly where they are in the "story" of your documentation.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Basic Anchor Links</h2>
                <p>
                    Create anchor links using the hash symbol (#) followed by an element's ID:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Simple Anchor Link Example</span>
                    </div>
                    <CodeBlock language="html" code={simpleAnchorCode} />
                </div>

                <div className="how-it-works">
                    <h3>How It Works:</h3>
                    <ol>
                        <li>Add unique <code>id</code> attribute to target element</li>
                        <li>Create link with <code>href="#id-name"</code></li>
                        <li>Clicking the link scrolls to that element</li>
                        <li>URL updates to include the fragment (e.g., <code>page.html#features</code>)</li>
                    </ol>
                </div>
            </div>

            <div className="content-section">
                <h2>Table of Contents Pattern</h2>
                <p>
                    A common pattern for long articles and documentation:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Table of Contents</span>
                    </div>
                    <CodeBlock language="html" code={tocCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>"Back to Top" Button</h2>
                <p>
                    Let users quickly return to the page top:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Back to Top Implementation</span>
                    </div>
                    <CodeBlock language="html" code={backToTopCode} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Pro Tip:</strong> You can link to <code>href="#"</code> or <code>href="#top"</code>
                        to scroll to the top, even without an ID. However, using an explicit ID is more semantic.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Smooth Scrolling</h2>
                <p>
                    Add smooth animated scrolling for better user experience:
                </p>

                <h3>CSS-Only Smooth Scrolling</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={cssSmoothScrollCode} />
                </div>

                <Warning><strong> Accessibility Consideration:</strong> Some users with vestibular disorders
                    prefer reduced motion. Respect their preferences:</Warning>

                <div className="code-example">
                    <div className="code-header">
                        <span>Respect User Preferences</span>
                    </div>
                    <CodeBlock language="css" code={mediaQueryScrollCode} />
                </div>

                <h3>JavaScript Smooth Scrolling (More Control)</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={jsSmoothScrollCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Scroll Offset for Fixed Headers</h2>
                <p>
                    When you have a fixed header, anchors can scroll behind it. Fix this with CSS:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Scroll Padding Top</span>
                    </div>
                    <CodeBlock language="css" code={scrollPaddingCode} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Alternative: Invisible Anchor Points</span>
                    </div>
                    <CodeBlock language="html" code={invisibleAnchorCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Highlighting Current Section</h2>
                <p>
                    Automatically highlight the current section in your navigation:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Intersection Observer for Active Links</span>
                    </div>
                    <CodeBlock language="javascript" code={intersectionObserverCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>FAQ Accordion with Anchors</h2>
                <p>
                    Link directly to specific FAQ items:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Linkable FAQ Items</span>
                    </div>
                    <CodeBlock language="html" code={faqAccordionCode} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>The :target Pseudo-Class:</strong> CSS automatically applies <code>:target</code>
                        to the element that matches the current URL fragment (the part after #).
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Styling the :target Element</h2>
                <p>
                    Use CSS to highlight the targeted element:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>:target Styling Examples</span>
                    </div>
                    <CodeBlock language="css" code={targetStylingCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>ID Naming Best Practices</h2>

                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Good ID Names</h3>
                        <ul>
                            <li><code>id="introduction"</code></li>
                            <li><code>id="getting-started"</code></li>
                            <li><code>id="section-2"</code></li>
                            <li><code>id="contact-form"</code></li>
                            <li><code>id="pricing-table"</code></li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Bad ID Names</h3>
                        <ul>
                            <li><code>id="1st-section"</code> (starts with number)</li>
                            <li><code>id="my section"</code> (contains space)</li>
                            <li><code>id="section#2"</code> (invalid character)</li>
                            <li><code>id="Contact"</code> (inconsistent casing)</li>
                            <li><code>id="a"</code> (not descriptive)</li>
                        </ul>
                    </div>
                </div>

                <h3>ID Rules:</h3>
                <ul>
                    <li>Must be unique on the page</li>
                    <li>Must start with a letter (a-z, A-Z)</li>
                    <li>Can contain letters, digits, hyphens, underscores, periods</li>
                    <li>Case-sensitive</li>
                    <li>No spaces allowed</li>
                    <li>Should be descriptive and meaningful</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>6. The Psychology of Navigation: Why Anchors Matter</h2>
                <p>
                    User experience studies show that visitors are often intimidated by
                    massive walls of text. Anchors serve as "Safety Nets." When a user sees
                    a Table of Contents or a "Jump to Summary" link, they feel more in
                    control of their reading experience.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Information Scent</h3>
                        <p>Descriptive anchors provide 'information scent'clues that tell
                            the user exactly what they will find when they click, reducing
                            cognitive load.</p>
                    </div>
                    <div className="concept-card">
                        <h3>The F-Pattern</h3>
                        <p>Users scan in an 'F' pattern. Placing anchors at key inflection
                            points allows scanners to stop and dive deep exactly where they
                            need to.</p>
                    </div>
                </div>

                <ProTip><p><strong>UX Rule:</strong> Never make an anchor link go to a
                        generic "Section 1" or "Part A". Use keyword-rich text like
                        "Jump to Technical Requirements" to help with SEO and user
                        confidence.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>7. Performance: Scrolling & The Browser Paint Loop</h2>
                <p>
                    Modern browsers optimize smooth scrolling, but poorly implemented
                    JavaScript scroll listeners can cause <strong>Layout Thrashing</strong>.
                    When an anchor link is clicked, the browser must:
                </p>
                <ol>
                    <li>Calculate the target element's bounding box.</li>
                    <li>Update the URL fragment.</li>
                    <li>Orchestrate the scroll animation (if <code>smooth</code>).</li>
                    <li>Fire <code>scroll</code> and <code>intersection</code> events.</li>
                </ol>

                <ProTip><p><strong>Performance Pro Tip:</strong> Use the CSS
                        <code>will-change: transform;</code> property on the body or
                        scrolling container if you are implementing complex parallax
                        effects alongside your anchors. This hints the browser to
                        promote the layer to the GPU, ensuring 60fps scrolling.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>8. Stateful Fragments: The Foundation of Client-Side Routing</h2>
                <p>
                    In Single Page Applications (SPAs), the URL fragment is the
                    primitive form of "State." By listening to the <code>hashchange</code>
                    event, developers can create complex navigation systems without
                    ever refreshing the page.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>A Simple Hash-Based Router</span>
                    </div>
                    <CodeBlock language="javascript" code={`// Simple Router Logic
window.addEventListener('hashchange', () => {
    const route = window.location.hash.slice(1) || 'home';
    loadContentForRoute(route);
});

function loadContentForRoute(route) {
    console.log(\`Updating state for: \${route}\`);
    // Update DOM dynamically based on fragment
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>9. Accessibility: The Focus Trap & Logical Flow</h2>
                <p>
                    Scroll-jumping can disorient users, especially those using screen
                    readers or keyboard navigation. When a user "jumps" to a section,
                    the visual viewport moves, but the <strong>keyboard focus</strong>
                    often stays on the link they just clicked. This is a "Focus Trap."
                </p>
                <ProTip><p><strong>Accessibility Requirement:</strong> Always ensure that
                        after a jump, the target element (or its container) receives
                        programmatic focus. This allows keyboard users to continue
                        reading from the new location immediately.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>10. Accessibility Best Practices</h2>

                <div className="accessibility-tips">
                    <h3>Make Anchors Accessible:</h3>
                    <ul>
                        <li>
                            <strong>Descriptive link text:</strong> "Jump to Features section" is better than "Click here"
                        </li>
                        <li>
                            <strong>Keyboard navigation:</strong> All anchor links should be keyboard-accessible
                        </li>
                        <li>
                            <strong>Focus management:</strong> Target element should receive focus after jump
                        </li>
                        <li>
                            <strong>Skip links:</strong> Provide "Skip to content" for keyboard users
                        </li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Focus Management</span>
                    </div>
                    <CodeBlock language="javascript" code={focusManagementCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Common Use Cases Summary</h2>

                <div className="use-cases">
                    <div className="use-case">
                        <h3> Documentation</h3>
                        <p>Table of contents, section navigation, API reference</p>
                    </div>
                    <div className="use-case">
                        <h3> Long Articles</h3>
                        <p>Skip to sections, back to top, related articles</p>
                    </div>
                    <div className="use-case">
                        <h3> E-commerce</h3>
                        <p>Product sections, reviews, specifications</p>
                    </div>
                    <div className="use-case">
                        <h3> FAQs</h3>
                        <p>Direct links to specific questions</p>
                    </div>
                    <div className="use-case">
                        <h3> Single Page Apps</h3>
                        <p>Section navigation, tabs, accordions</p>
                    </div>
                    <div className="use-case">
                        <h3> Educational</h3>
                        <p>Course modules, lesson sections, exercises</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use descriptive, unique IDs</li>
                            <li>Add smooth scrolling with <code>scroll-behavior</code></li>
                            <li>Respect <code>prefers-reduced-motion</code></li>
                            <li>Account for fixed headers with scroll offset</li>
                            <li>Highlight targeted sections with <code>:target</code></li>
                            <li>Test keyboard navigation</li>
                            <li>Manage focus after scrolling</li>
                            <li>Provide back to top links for long pages</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use duplicate IDs on same page</li>
                            <li>Start IDs with numbers</li>
                            <li>Include spaces in IDs</li>
                            <li>Forget about fixed header overlap</li>
                            <li>Use anchor links for actions (use buttons)</li>
                            <li>Ignore accessibility considerations</li>
                            <li>Make anchor links too small to click</li>
                            <li>Forget to test smooth scrolling</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLBookmarks;


